import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React, {useState} from 'react';

import PillSelect, {PillSelectOption} from 'app/components/PillSelect';
import colors from 'app/styles/colors.json';

import cs from './AgentExploreNote.module.scss';

export interface AgentExploreNoteProps {
  content: React.ReactNode;
  /** Raw text shown in the editor when saving as a note. */
  text: string;
  /** Author choices for the saved note, e.g. the agent or the current user. */
  authorOptions: PillSelectOption[];
  defaultAuthorValue: string;
  /** Highlights the card as the focused observation. */
  selected?: boolean;
  /** Marks the card as saved as a note. */
  saved?: boolean;
  /** Marks the card as dismissed. */
  dismissed?: boolean;
  /** Absent when the card has nowhere to go; it then reads as static text. */
  onClick?: () => void;
  onSaveAsNote: (args: {text: string; userId: string}) => void;
  onDismiss: () => void;
  /** Un-dismisses a note, returning it to its un-acted-on state. */
  onRestore: () => void;
  canEdit: boolean;
}

const AgentExploreNote: React.FC<AgentExploreNoteProps> = ({
  content,
  text,
  authorOptions,
  defaultAuthorValue,
  selected,
  saved,
  dismissed,
  onClick,
  onSaveAsNote,
  onDismiss,
  onRestore,
  canEdit,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState(text);
  const [authorValue, setAuthorValue] = useState(defaultAuthorValue);

  const actionButtons = () => {
    switch (true) {
      case !canEdit:
        return <></>;
      case isEditing:
        return (
          <>
            <B.Button
              small
              minimal
              className={cs.dismissButton}
              onClick={(e) => {
                e.stopPropagation();
                setDraftText(text);
                setAuthorValue(defaultAuthorValue);
                setIsEditing(false);
              }}
            >
              Cancel
            </B.Button>
            <B.Button
              small
              intent="primary"
              className={cs.saveButton}
              minimal
              onClick={(e) => {
                e.stopPropagation();
                onSaveAsNote({text: draftText, userId: authorValue});
                setIsEditing(false);
              }}
            >
              Save as note
            </B.Button>
          </>
        );
      case dismissed:
        return (
          <B.Button
            small
            intent="primary"
            className={cs.saveButton}
            minimal
            icon="undo"
            onClick={(e) => {
              e.stopPropagation();
              onRestore();
            }}
          >
            Restore
          </B.Button>
        );
      case saved:
        return (
          <>
            <B.Button
              small
              minimal
              className={cs.dismissButton}
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
            >
              Dismiss
            </B.Button>
            <B.Button small intent="success" minimal disabled icon="tick">
              Saved!
            </B.Button>
          </>
        );
      default:
        return (
          <>
            <B.Button
              small
              minimal
              className={cs.dismissButton}
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
            >
              Dismiss
            </B.Button>
            <B.Button
              small
              intent="primary"
              className={cs.saveButton}
              minimal
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
            >
              Edit & save as note
            </B.Button>
          </>
        );
    }
  };

  return (
    <div
      className={classnames(cs.agentExploreNote, {
        [cs.selected]: selected,
        [cs.saved]: saved && !isEditing,
        [cs.dismissed]: dismissed && !isEditing,
        [cs.static]: !onClick,
      })}
      aria-current={selected ? 'true' : undefined}
      onClick={isEditing || dismissed ? undefined : onClick}
    >
      {isEditing ? (
        <div onClick={(e) => e.stopPropagation()}>
          <B.TextArea
            className={cs.editTextArea}
            value={draftText}
            growVertically
            fill
            autoFocus
            onChange={(e) => setDraftText(e.target.value)}
          />
          <div className={cs.authorRow}>
            <div>Author:</div>
            <PillSelect
              accentColor={colors.purple}
              options={authorOptions}
              value={authorValue}
              onChange={setAuthorValue}
            />
          </div>
        </div>
      ) : (
        <div className={cs.noteContent}>{content}</div>
      )}

      {canEdit && (
        <div className={cs.noteFooter}>
          <div className={cs.noteActions}>{actionButtons()}</div>
        </div>
      )}
    </div>
  );
};

export default AgentExploreNote;
